import React, { MouseEventHandler } from 'react'
import Icon, { IconName } from 'particles/Icon'
import Text, { TextColor } from 'particles/Text'
import Tooltip from 'particles/Tooltip'
import { Theme } from 'particles/themes'
import styled from 'particles/themes/styled'
import { IllustrativeIcon, IllustrativeIconType } from '../Campaign/Template/ObjectiveIcon'

interface CreationMethodCardProps {
    title: string
    description: string
    tag?: {
        label: string
        tooltipContent: string
        iconName?: IconName
    }
    iconType: IllustrativeIconType
    isSelected?: boolean
    isDisabled?: boolean
    onClick?: MouseEventHandler<HTMLDivElement>
}

interface TextColorObject {
    title: TextColor
    description: TextColor
}

const CreationMethodCard: React.FC<CreationMethodCardProps> = ({
    title,
    description,
    tag,
    iconType,
    isDisabled = false,
    isSelected = false,
    onClick,
    ...props
}) => {
    const textColor = (() => {
        const textColorObj: TextColorObject = {
            title: 'primary',
            description: 'secondary',
        }
        if (isDisabled) {
            textColorObj.title = 'disable'
            textColorObj.description = 'disable'
        } else if (isSelected) {
            textColorObj.title = 'primaryInverse'
            textColorObj.description = 'primaryInverse'
        }

        return textColorObj
    })()

    return (
        <CreationMethodContainer
            isDisabled={isDisabled}
            isSelected={isSelected}
            onClick={!isDisabled ? onClick : undefined}
            {...props}
        >
            <div className="icon-container">
                <IllustrativeIcon type={iconType} className="icon" />
            </div>
            <div className="content-container">
                <div className="title">
                    <Text className="text" scale="bodyM500" color={textColor.title}>
                        {title}
                    </Text>

                    {tag?.label && (
                        <Tooltip label={tag.tooltipContent}>
                            <Tag isDisabled={isDisabled}>
                                {tag.iconName && (
                                    <Icon
                                        size="16px"
                                        name={tag.iconName}
                                        color={isDisabled ? 'disable' : 'actionInverse'}
                                    />
                                )}
                                <Text scale="caption500" color={isDisabled ? 'disable' : 'actionInverse'}>
                                    {tag.label}
                                </Text>
                            </Tag>
                        </Tooltip>
                    )}
                </div>

                <Text className="text" scale="bodyS400" color={textColor.description}>
                    {description}
                </Text>
            </div>
        </CreationMethodContainer>
    )
}

export default CreationMethodCard

interface CreationMethodProps {
    isDisabled?: boolean
    isSelected?: boolean
}

const getBackgroundColor = (theme: Theme, isDisabled?: boolean, isSelected?: boolean) => {
    if (isDisabled) {
        return theme.colors.surface.disable
    }

    if (isSelected) {
        return theme.colors.surface.action
    }

    // return null, so can set dynamic default value after function call
    return null
}

const CreationMethodContainer = styled.div<CreationMethodProps>`
    box-sizing: border-box;
    width: 100%;
    min-height: 86px;
    border-radius: 8px;
    padding: 12px;
    display: flex;
    gap: ${({ theme }) => theme.spacing[300]};
    align-items: center;
    outline: ${({ isDisabled, isSelected }) => (isDisabled || isSelected ? '0px' : '1px solid')};
    outline-color: ${({ theme }) => theme.colors.surface.primary};
    background: ${({ theme, isSelected, isDisabled }) =>
        getBackgroundColor(theme, isDisabled, isSelected) || 'transparent'};
    &:hover {
        background: ${({ theme, isDisabled, isSelected }) =>
            getBackgroundColor(theme, isDisabled, isSelected) || theme.colors.surface.actionPastelHover};
    }
    &:active {
        background: ${({ theme, isDisabled, isSelected }) =>
            getBackgroundColor(theme, isDisabled, isSelected) || theme.colors.surface.action};
        .text {
            color: ${({ theme }) => theme.colors.text.primaryInverse};
        }
    }

    .title {
        display: flex;
        align-items: center;
        gap: ${({ theme }) => theme.spacing[300]};
    }

    .icon-container {
        width: 62px;
        height: 62px;
        border-radius: 8px;
        padding: 12px;
        background: ${({ theme, isDisabled }) =>
            isDisabled ? theme.colors.surface.disable : theme.colors.surface.infoPastel};
        box-sizing: border-box;

        .icon {
            width: 38px;
            height: 38px;
        }
    }

    .content-container {
        display: flex;
        flex-direction: column;
        gap: ${({ theme }) => theme.spacing[100]};
    }

    @media (max-width: 768px) {
        .title {
            justify-content: space-between;
        }
    }
`

const Tag = styled.div<CreationMethodProps>`
    display: flex;
    gap: ${({ theme }) => theme.spacing[100]};
    border-radius: 4px;
    align-items: center;
    height: 24px;
    background-color: ${({ theme, isDisabled }) =>
        isDisabled ? theme.colors.surface.primary : theme.colors.surface.positive};
    padding: 0px 6px;
    width: fit-content;
    flex-shrink: 0;
`
